import React, { useState } from 'react'
import { connect } from 'react-redux'
import videoServices from 'services/video'
import Question from 'components/question'
import Video from 'components/video/video'
import { mapLanguageToComponentProps } from 'i18n'
import { Body, Hr, Small, Subtitle } from 'components/styled/typography'
import styled, { css } from 'styled-components'
import { CardContainer, CardGroup } from 'components/styled/card-group'
import { Box } from 'components/styled/containers/box'
import { QuestionType } from 'types'
import { I18nComponentType } from 'i18n/i18n-types'
import { ThemeSpace } from 'theme/spaces'
import { Button } from 'components/styled/button'
import { Flex } from 'components/styled/containers/flex'
import { Confirmation } from 'components/styled/modal/confirmation'
import AiSummaryBox from 'components/styled/ai-summary-box'
import { Icon } from 'components/styled/icon'
import { Field } from 'components/styled/typography/field'

type ResponseType = {
  _id: string
  questionId: string
  videoUrl?: string
  playbackVideoUrl?: string
  recorder?: string
  saved: boolean
}

type TranscriptionType = {
  _id: string
  videoUrl: string
  summary?: string
  transcriptionText?: string
  aiInsights?: string
  transcriptionJobStatus?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
}

interface QuestionsAndResponsesProps {
  i18n: I18nComponentType
  questions: QuestionType[]
  responses?: ResponseType[]
  transcriptions?: TranscriptionType[]
  onResponsePlay?: () => void
  cardMinWidth?: ThemeSpace
  resetQuestionResponse?: (questionId: string) => void
  resetResponseEventProps?: any
  resettingResponse?: boolean
  locationId?: string
  videoEventProps?: any
  fastScreeningMode?: boolean
}

const NoResponseWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 75%;
`

const NoResponse = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: ${({ theme }) => theme.space.s12};
  background-color: ${({ theme }) => theme.color.grey05};
`

function refreshPlaybackUrl(
  response: ResponseType,
  useOpentokPreview: boolean
) {
  return videoServices.fetchPlaybackUrl(response, useOpentokPreview)
}

const NoQuestionShownStyles = css`
  display: flex;
  gap: ${({ theme }) => theme.space.s20};
  align-items: flex-start;

  ${({ theme }) => theme.mediaQuery.screenS} {
    flex-direction: column;
    > * {
      width: 100%;
    }
  }
`

const QuestionContainer = styled(Flex)`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: flex-start;

  ${({ theme }) => theme.mediaQuery.screenS} {
    flex-direction: column;
    gap: ${({ theme }) => theme.space.s24};
  }
`

const StyledQuestionText = styled(Body)`
  font-size: ${({ theme }) => theme.space.s14};
`

const VideoContainer = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s8};

  ${({ theme }) => theme.mediaQuery.screenS} {
    width: 100%;
  }
`

const SummaryContainer = styled(Flex)<{ $showQuestionVideo?: boolean }>`
  flex: 1 1 calc(50% - ${({ theme }) => theme.space.s20} / 2);
  max-width: calc(50% - ${({ theme }) => theme.space.s20} / 2);
  min-width: 0;
  margin-top: ${({ theme, $showQuestionVideo }) =>
    $showQuestionVideo && theme.space.s16};

  ${({ $showQuestionVideo }) =>
    $showQuestionVideo &&
    css`
      flex: 2;
      max-width: none;
    `}

  ${({ theme }) => theme.mediaQuery.screenS} {
    width: 100%;
    flex: 1 1 auto;
    max-width: none;
  }
`

const StyledFastScreeningModeWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.space.s40};
`

const StyledCardGroupWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.space.s16};

  ${({ theme }) => theme.mediaQuery.screenS} {
    margin-bottom: ${({ theme }) => theme.space.s24};
  }
`

const FastModeContainer = styled.div<{
  $showQuestionVideo?: boolean
  $hasAnswerAndTranscription?: boolean
}>`
  width: 100%;
  margin-bottom: 0;
  display: block;

  ${CardContainer} {
    flex: 1 1 calc(50% - ${({ theme }) => theme.space.s20} / 2);
    max-width: calc(50% - ${({ theme }) => theme.space.s20} / 2);
    min-width: 0;
    padding-top: ${({ $hasAnswerAndTranscription }) =>
      !$hasAnswerAndTranscription && '0rem !important'};
  }

  ${({ $showQuestionVideo }) =>
    !$showQuestionVideo &&
    css`
      ${CardContainer} {
        grid-template-columns: minmax(0, 1fr);
      }
    `}

  ${({ $showQuestionVideo }) =>
    $showQuestionVideo &&
    css`
      ${CardContainer} {
        flex: 1;
        max-width: none;
      }
    `}

  ${({ theme }) => theme.mediaQuery.screenS} {
    ${CardContainer} {
      flex: 1 1 auto;
      max-width: none;
    }
  }

  ${({ $showQuestionVideo }) => !$showQuestionVideo && NoQuestionShownStyles};
`

export function QuestionsAndResponses({
  questions,
  responses = [],
  transcriptions = [],
  onResponsePlay,
  i18n,
  cardMinWidth = 's400',
  resetQuestionResponse,
  resetResponseEventProps,
  resettingResponse,
  locationId,
  videoEventProps,
  fastScreeningMode = false,
}: QuestionsAndResponsesProps) {
  const [questionToReset, setQuestionToReset] = useState<QuestionType>()
  const [visibleQuestionVideoId, setVisibleQuestionVideoId] = useState<
    string | null
  >(null)
  const [questionTextToggled, setQuestionTextToggled] = useState<boolean>(false)
  // TODO: translations
  // TODO: How to display response video?

  return (
    <div className="questions-and-responses">
      {questions
        ? questions.map((question, index) => {
            const response = responses.find(
              (res) => res.saved && res.questionId === question._id
            )
            const pureVideoUrl = response?.videoUrl?.split('?')[0]
            const transcription = pureVideoUrl
              ? transcriptions?.find(
                  (item) => item?.videoUrl === pureVideoUrl
                )
              : undefined

            const transcriptionCompletedWithNoSummaryAndInsights =
              transcription &&
              transcription.transcriptionJobStatus === 'COMPLETED' &&
              !transcription?.summary &&
              !transcription?.aiInsights

            const showQuestionVideo = !!(
              visibleQuestionVideoId === question._id ||
              transcriptionCompletedWithNoSummaryAndInsights
            )

            const hasQuestionText = !!(
              question.transcriptionText || question.textQuestion
            )

            const isLastQuestion =
              questions.indexOf(question) === questions.length - 1

            const hasAnswerAndTranscription = !!(
              question.answerable &&
              response &&
              response.videoUrl &&
              transcription
            )

            const showFastScreeningMode =
              fastScreeningMode &&
              (hasAnswerAndTranscription || !question.answerable)

            const questionLabel = `${i18n.questionsAndResponses.question} ${
              index + 1
            }`

            const summaryContainer = (showQuestionVideo: boolean) => (
              <SummaryContainer
                $showQuestionVideo={showQuestionVideo}
                flexDirection="column"
                gap="s16"
              >
                <AiSummaryBox
                  text={transcription?.summary || ''}
                  type="summary"
                  isLoading={
                    transcription?.transcriptionJobStatus !== 'COMPLETED'
                  }
                />
                {/* insights come here */}
                <AiSummaryBox
                  label={i18n.questionsAndResponses.aiInsights}
                  text={transcription?.aiInsights || ''}
                  type="summary"
                  isLoading={
                    transcription?.transcriptionJobStatus !== 'COMPLETED'
                  }
                />
              </SummaryContainer>
            )

            if (showFastScreeningMode) {
              return (
                <StyledFastScreeningModeWrapper key={question._id}>
                  <QuestionContainer
                    alignItems="center"
                    gap="s12"
                    mt="s32"
                    mb="s24"
                    justifyContent="space-between"
                  >
                    <Flex flexDirection="column" gap="s4">
                      <Subtitle fontWeight={600}>
                        {question.title
                          ? `${questionLabel}: ${question.title}`
                          : questionLabel}
                      </Subtitle>
                      {!question.answerable && (
                        <Small fontStyle="italic">
                          {i18n.questionsAndResponses.nonAnswerableInfo}
                        </Small>
                      )}
                      {hasQuestionText && (
                        <div
                          role="button"
                          onClick={() =>
                            setQuestionTextToggled(!questionTextToggled)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setQuestionTextToggled(!questionTextToggled)
                            }
                          }}
                          tabIndex={0}
                        >
                          <StyledQuestionText
                            ellipsis
                            numberOfLines={questionTextToggled ? undefined : 2}
                          >
                            {question.transcriptionText ||
                              question.textQuestion}
                          </StyledQuestionText>
                        </div>
                      )}
                    </Flex>
                    {question.videoUrl &&
                      (!hasAnswerAndTranscription ||
                        !transcriptionCompletedWithNoSummaryAndInsights) && (
                        <Flex
                          gap="s4"
                          alignItems="center"
                          // @ts-ignore
                          onClick={() => {
                            if (visibleQuestionVideoId === question._id) {
                              setVisibleQuestionVideoId(null)
                            } else {
                              setVisibleQuestionVideoId(question._id || null)
                            }
                          }}
                          // @ts-ignore
                          title={
                            showQuestionVideo
                              ? i18n.questionsAndResponses.hideQuestion
                              : i18n.questionsAndResponses.showQuestion
                          }
                        >
                          {showQuestionVideo
                            ? i18n.questionsAndResponses.hideQuestion
                            : i18n.questionsAndResponses.showQuestion}
                          <Icon
                            type={
                              showQuestionVideo ? 'expand_less' : 'expand_more'
                            }
                          />
                        </Flex>
                      )}
                  </QuestionContainer>
                  {question.evaluationCriteria && (
                    <Field
                      title={i18n.questionsAndResponses.evaluationCriteriaLabel}
                      content={question.evaluationCriteria}
                      expandFromIcon
                    />
                  )}

                  <FastModeContainer
                    $showQuestionVideo={showQuestionVideo}
                    $hasAnswerAndTranscription={hasAnswerAndTranscription}
                  >
                    <CardGroup minWidth={cardMinWidth}>
                      {question.videoUrl && showQuestionVideo && (
                        <VideoContainer>
                          <Question
                            locationId={locationId}
                            videoEventProps={videoEventProps}
                            question={question}
                            className="radius"
                          />
                        </VideoContainer>
                      )}
                      {hasAnswerAndTranscription && (
                        <VideoContainer>
                          <Video
                            id={response._id}
                            title={i18n.questionsAndResponses.response}
                            videoUrl={response.videoUrl}
                            recorder={response.recorder}
                            playbackVideoUrl={response.playbackVideoUrl}
                            refreshPlaybackUrl={(useOpentokPreview: boolean) =>
                              refreshPlaybackUrl(response, useOpentokPreview)
                            }
                            onPlay={onResponsePlay}
                            locationId={locationId}
                            eventId="responseVideoPlayButton"
                            videoEventProps={videoEventProps}
                          />
                          {resetQuestionResponse && (
                            <Flex mt="s16" justifyContent="flex-start">
                              <Button
                                variant="primaryGhost"
                                icon="delete"
                                loading={resettingResponse}
                                text={
                                  i18n.questionsAndResponses
                                    .resetQuestionResponse
                                }
                                onClick={() => setQuestionToReset(question)}
                                event={{
                                  locationId: 'adminCandidatePage',
                                  id: 'deleteResponseButton',
                                  properties: resetResponseEventProps,
                                }}
                              />
                            </Flex>
                          )}
                          <AiSummaryBox
                            text={transcription?.transcriptionText || ''}
                            type="transcript"
                            initialHideText
                            isLoading={
                              transcription?.transcriptionJobStatus !==
                              'COMPLETED'
                            }
                          />
                        </VideoContainer>
                      )}
                    </CardGroup>
                    {hasAnswerAndTranscription &&
                      summaryContainer(showQuestionVideo)}
                  </FastModeContainer>

                  <Hr />
                </StyledFastScreeningModeWrapper>
              )
            }

            return (
              <StyledCardGroupWrapper key={question._id}>
                {fastScreeningMode && (
                  <Subtitle fontWeight={600}>
                    {question.title
                      ? `${questionLabel}: ${question.title}`
                      : questionLabel}
                  </Subtitle>
                )}
                {question.evaluationCriteria && (
                  <Field
                    title={i18n.questionsAndResponses.evaluationCriteriaLabel}
                    content={question.evaluationCriteria}
                    expandFromIcon
                  />
                )}
                <CardGroup minWidth={cardMinWidth}>
                  <VideoContainer>
                    <Question
                      locationId={locationId}
                      videoEventProps={videoEventProps}
                      question={question}
                      className="radius"
                    />
                    <AiSummaryBox
                      text={transcription?.transcriptionText || ''}
                      type="transcript"
                    />
                  </VideoContainer>
                  {question.answerable && (
                    <VideoContainer>
                      {response && response.videoUrl ? (
                        <>
                          <Video
                            id={response._id}
                            title={i18n.questionsAndResponses.response}
                            videoUrl={response.videoUrl}
                            recorder={response.recorder}
                            playbackVideoUrl={response.playbackVideoUrl}
                            refreshPlaybackUrl={(useOpentokPreview: boolean) =>
                              refreshPlaybackUrl(response, useOpentokPreview)
                            }
                            onPlay={onResponsePlay}
                            locationId={locationId}
                            eventId="responseVideoPlayButton"
                            videoEventProps={videoEventProps}
                          />
                          {resetQuestionResponse && (
                            <Flex mt="s16" justifyContent="flex-end">
                              <Button
                                variant="primaryGhost"
                                icon="delete"
                                loading={resettingResponse}
                                text={
                                  i18n.questionsAndResponses
                                    .resetQuestionResponse
                                }
                                onClick={() => setQuestionToReset(question)}
                                event={{
                                  locationId: 'adminCandidatePage',
                                  id: 'deleteResponseButton',
                                  properties: resetResponseEventProps,
                                }}
                              />
                            </Flex>
                          )}
                        </>
                      ) : (
                        <NoResponseWrapper>
                          <NoResponse>
                            <Subtitle>
                              {i18n.questionsAndResponses.noResponse}
                            </Subtitle>
                          </NoResponse>
                        </NoResponseWrapper>
                      )}
                      <AiSummaryBox
                        text={transcription?.summary || ''}
                        type="summary"
                      />
                    </VideoContainer>
                  )}
                </CardGroup>
                <Hr />
              </StyledCardGroupWrapper>
            )
          })
        : null}
      {questionToReset && resetQuestionResponse && (
        <Confirmation
          id="confirm-reset-question-response"
          isOpen={!!questionToReset}
          onConfirm={() => {
            setQuestionToReset(undefined)
            resetQuestionResponse(questionToReset._id || '')
          }}
          onCancel={() => setQuestionToReset(undefined)}
          title={i18n.questionsAndResponses.resetQuestionResponseConfirm(
            questionToReset.title || questionToReset.textQuestion
          )}
          body={i18n.questionsAndResponses.resetQuestionResponseConfirm2}
          isLoading={resettingResponse}
        />
      )}
    </div>
  )
}

export default connect(mapLanguageToComponentProps(() => ({})))(
  QuestionsAndResponses
)
